import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { LoginPage } from '@/features/auth/components/login-page';
import { LoadingScreen } from '@/components/ui/loading';
import { safeRedirectTarget } from '@/lib/safe-redirect-target';
import { RegisterPage } from '@/features/auth/components/register-page';
import { DashboardPage } from '@/features/dashboard/components/dashboard-page';
import { PropertiesPage } from '@/features/properties/components/properties-page';
import { PropertyPortal } from '@/features/properties/components/property-portal';
import { PropertyScheduledReportsPage } from '@/features/properties/components/property-scheduled-reports-page';
import { AdminGatewaysPage } from '@/features/gateways/components/admin-gateways-page';
import { SaltUnitTypesPage } from '@/features/admin/salt-unit-types/components/salt-unit-types-page';
import { DevicesPage } from '@/features/devices/components/devices-page';
import { DeviceDetailPage } from '@/features/devices/components/device-detail-page';
import { AlertsPage } from '@/features/alerts/components/alerts-page';
import { UsersPage } from '@/features/users/components/users-page';
import { UserDetailPage } from '@/features/users/components/user-detail-page';
import { UnitDetailPage } from '@/features/units/components/unit-detail-page';
import { GatewayDetailPage } from '@/features/gateways/components/gateway-detail-page';
import { AuditLogsPage } from '@/features/audit-logs/components/audit-logs-page';
import { DeviceDatabasePage } from '@/features/device-database/components/DeviceDatabasePage';
import { BulkSetupModePage } from '@/features/setup/components/bulk-setup-mode-page';
import { OrganizationsPage } from '@/features/organizations/components/organizations-page';
import { OrganizationDetailPage } from '@/features/organizations/components/organization-detail-page';
import { NoOrgPage } from '@/features/auth/components/no-org-page';
import { AcceptInvitePage } from '@/features/auth/components/accept-invite-page';
import { ForgotPasswordPage } from '@/features/auth/components/forgot-password-page';
import { ResetPasswordPage } from '@/features/auth/components/reset-password-page';
import { SetupPairLanding } from '@/features/pairing/components/setup-pair-landing';
import { PhoneScannerPage } from '@/features/pairing/components/phone-scanner-page';
import { AuthLayout } from '@/components/layouts/auth-layout';
import { SidebarLayout } from '@/components/layouts/sidebar-layout';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { UserRole } from '@/types/api';

const EMPTY_ORG_ID = '00000000-0000-0000-0000-000000000000';

// Build a /login URL that preserves the attempted path so the user is
// forwarded back after authenticating. Used by every protected-route
// guard so deep links (e.g., from scheduled-report emails) survive a
// detour through the login screen.
function useLoginRedirectTo() {
  const location = useLocation();
  const attempted = `${location.pathname}${location.search}${location.hash}`;
  // Skip the dance if the attempted path is itself /login or unsafe —
  // we don't want a redirect loop, and safeRedirectTarget will reject
  // anything bogus on the way back.
  if (!attempted.startsWith('/') || attempted.startsWith('/login')) return '/login';
  return `/login?redirect=${encodeURIComponent(attempted)}`;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const loginTo = useLoginRedirectTo();

  if (isLoading) return <LoadingScreen />;

  if (!user) {
    return <Navigate to={loginTo} replace />;
  }

  if (!user.organizationId || user.organizationId === EMPTY_ORG_ID) {
    return <Navigate to="/no-org" replace />;
  }

  return <SidebarLayout>{children}</SidebarLayout>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const loginTo = useLoginRedirectTo();

  if (isLoading) return <LoadingScreen />;

  if (!user) return <Navigate to={loginTo} replace />;

  if (!user.organizationId || user.organizationId === EMPTY_ORG_ID) {
    return <Navigate to="/no-org" replace />;
  }

  if (user.role !== UserRole.Admin && user.role !== UserRole.Owner) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SidebarLayout>{children}</SidebarLayout>;
}

// Admin + Owner + Manager. Used for surfaces that touch org-level people
// (Users list/detail) — Viewer is read-only and doesn't need this lens.
function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const loginTo = useLoginRedirectTo();

  if (isLoading) return <LoadingScreen />;

  if (!user) return <Navigate to={loginTo} replace />;

  if (!user.organizationId || user.organizationId === EMPTY_ORG_ID) {
    return <Navigate to="/no-org" replace />;
  }

  if (
    user.role !== UserRole.Admin &&
    user.role !== UserRole.Owner &&
    user.role !== UserRole.Manager
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SidebarLayout>{children}</SidebarLayout>;
}

function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const loginTo = useLoginRedirectTo();

  if (isLoading) return <LoadingScreen />;

  if (!user) return <Navigate to={loginTo} replace />;
  if (user.role !== UserRole.Owner) return <Navigate to="/dashboard" replace />;
  return <SidebarLayout>{children}</SidebarLayout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  if (isLoading) return <LoadingScreen />;

  if (user) {
    // Honor ?redirect= so already-authenticated users following an
    // email deep link land on the intended page instead of /dashboard.
    const target = safeRedirectTarget(searchParams.get('redirect')) ?? '/dashboard';
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><AuthLayout><LoginPage /></AuthLayout></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><AuthLayout><RegisterPage /></AuthLayout></PublicRoute>} />
      <Route path="/no-org" element={<NoOrgPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<PublicRoute><AuthLayout><ForgotPasswordPage /></AuthLayout></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><AuthLayout><ResetPasswordPage /></AuthLayout></PublicRoute>} />
      <Route path="/setup/pair" element={<SetupPairLanding />} />

      {/* Protected routes */}
      <Route path="/pair/scanner" element={<ProtectedRoute><PhoneScannerPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:propertyId" element={<ProtectedRoute><PropertyPortal /></ProtectedRoute>} />
      <Route path="/properties/:propertyId/scheduled-reports" element={<ProtectedRoute><PropertyScheduledReportsPage /></ProtectedRoute>} />
      <Route path="/admin/gateways" element={<OwnerRoute><AdminGatewaysPage /></OwnerRoute>} />
      <Route path="/admin/salt-unit-types" element={<OwnerRoute><SaltUnitTypesPage /></OwnerRoute>} />
      <Route path="/gateways/:id" element={<ProtectedRoute><GatewayDetailPage /></ProtectedRoute>} />
      <Route path="/units/:id" element={<ProtectedRoute><UnitDetailPage /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><DevicesPage /></ProtectedRoute>} />
      <Route path="/devices/:id" element={<ProtectedRoute><DeviceDetailPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><BulkSetupModePage /></ProtectedRoute>} />
      <Route path="/users" element={<ManagerRoute><UsersPage /></ManagerRoute>} />
      <Route path="/users/:id" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
      <Route path="/audit-logs" element={<AdminRoute><AuditLogsPage /></AdminRoute>} />
      <Route path="/organizations" element={<AdminRoute><OrganizationsPage /></AdminRoute>} />
      <Route path="/organizations/:id" element={<AdminRoute><OrganizationDetailPage /></AdminRoute>} />
      <Route path="/device-database" element={<OwnerRoute><DeviceDatabasePage /></OwnerRoute>} />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
